import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CoordinatorDeckGLMap from '../components/CoordinatorDeckGLMap';
import CoordinatorFilterPanel, { MarkerDetailColumn } from '../components/CoordinatorFilterPanel';
import TicketChatPanel from '../components/TicketChatPanel';
import { MarkerActionMenu, CheckInRequestDialog } from '../components/MarkerActionMenu';
import {
  fetchCoordinatorTickets,
  fetchOpenMessageCounts,
  fetchCoordinatorFieldReps,
  refreshCoordinatorFieldReps,
  fetchCoordinatorIcons,
  checkCoordinatorAdmin,
  fetchMapConfig,
  fetchCacheStatus,
  fetchCoordinatorLiveReps,
} from '../services/api';
import { resolveTicketIcon } from '../utils/iconResolver';
import { slugifyTicketType } from '../utils/slugify';
import { Link } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import './CoordinatorMap.css';

const DEFAULT_FILTERS = {
  search: '',            // case-insensitive substring across recordId / site / description / city
  projectNames: null,    // null = all selected, Set = specific selections
  serviceTypes: null,
  states: null,
  fscValues: null,
  fieldRepNames: null,
  statuses: null,
  dueDateFrom: null,
  dueDateTo: null,
  startDateFrom: null,
  startDateTo: null,
  scheduleDateFrom: null,
  scheduleDateTo: null,
  repRatingTiers: new Set([0, 1, 2, 3, 4, 5]),
  repPaymentMethod: null,
  repCategory: null,     // null = all; Set = specific Rep Category values
  classification: null,  // null = all; Set = specific Classification values
  repMinJobs12mo: 0,     // minimum jobs completed in the last 12 months
  // Completed-project history filter (Apply queries QB → sets repHistoryRepIds)
  repHistoryProjectId: null,
  repHistoryDateFrom: null,
  repHistoryDateTo: null,
  repHistoryRepIds: null, // null = inactive; Set of repIds after Apply
  repHistoryTotalVisits: null,
  preset: null,          // 'scheduledToday' | 'pastDue' | 'checkedIn' | null
};

// Split a QB multitext value (comma-joined string) into trimmed values.
const splitMulti = s => (s ? String(s).split(',').map(v => v.trim()).filter(Boolean) : []);

// Sentinel option matching reps with no value in a multitext field.
const NO_VALUE = '(None)';

const HIDDEN_TYPES_KEY = 'coordHiddenTicketTypes';

function dateKey(value) {
  if (!value) return '';
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

function persistHiddenTypes(next) {
  try {
    if (next.size === 0) sessionStorage.removeItem(HIDDEN_TYPES_KEY);
    else sessionStorage.setItem(HIDDEN_TYPES_KEY, JSON.stringify([...next]));
  } catch { /* ignore */ }
}

function loadHiddenTypeSlugs() {
  try {
    const raw = sessionStorage.getItem(HIDDEN_TYPES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

const LABEL_PREFS_KEY = 'coordLabelPrefs';
const FILTERS_STORAGE_KEY = 'coordMapFilters';
const LAYERS_STORAGE_KEY = 'coordMapLayers';
const DEFAULT_LABEL_PREFS = {
  ticketLabels: true,
  fieldRepLabels: true,
  liveRepLabels: true,
  ticketLabelField: 'siteName',
};

/** Filter keys stored as Sets (null = all selected for most of these). */
const FILTER_SET_KEYS = [
  'projectNames', 'serviceTypes', 'states', 'fscValues', 'fieldRepNames', 'statuses',
  'repRatingTiers', 'repPaymentMethod', 'repCategory', 'classification', 'repHistoryRepIds',
];

function loadLabelPrefs() {
  try {
    const raw = localStorage.getItem(LABEL_PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ticketLabels: parsed.ticketLabels !== false,
      fieldRepLabels: parsed.fieldRepLabels !== false,
      liveRepLabels: parsed.liveRepLabels !== false,
      ticketLabelField: typeof parsed.ticketLabelField === 'string'
        ? parsed.ticketLabelField
        : DEFAULT_LABEL_PREFS.ticketLabelField,
    };
  } catch {
    return null;
  }
}

function serializeFilters(filters) {
  const out = { ...filters };
  for (const k of FILTER_SET_KEYS) {
    if (out[k] instanceof Set) out[k] = { __set: [...out[k]] };
  }
  return JSON.stringify(out);
}

function loadStoredFilters() {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const out = { ...DEFAULT_FILTERS, ...parsed };
    for (const k of FILTER_SET_KEYS) {
      if (parsed[k] && Array.isArray(parsed[k].__set)) {
        out[k] = new Set(parsed[k].__set);
      } else if (parsed[k] === null) {
        out[k] = null;
      } else if (k === 'repRatingTiers') {
        out[k] = DEFAULT_FILTERS.repRatingTiers;
      }
    }
    // repHistoryRepIds is session-scoped query state — drop on reload so we
    // don't show an empty map from a stale Apply without re-querying QB.
    out.repHistoryRepIds = null;
    out.repHistoryTotalVisits = null;
    return out;
  } catch {
    return null;
  }
}

function loadStoredLayers() {
  try {
    const raw = sessionStorage.getItem(LAYERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      unassigned: parsed.unassigned !== false,
      assigned: !!parsed.assigned,
      fieldReps: !!parsed.fieldReps,
      liveReps: parsed.liveReps !== false,
    };
  } catch {
    return null;
  }
}

/**
 * After a save, keep the user's filter selection intact while ensuring the
 * edited ticket can still appear (new rep name / status added to active Sets;
 * Assigned layer turned on when a ticket flips unassigned → assigned).
 */
function adjustFiltersAfterTicketUpdate(filters, before, updated) {
  let next = filters;
  const oldRep = before.fieldRepName || '';
  const newRep = updated.fieldRepName || '';
  if (filters.fieldRepNames && oldRep !== newRep && !filters.fieldRepNames.has(newRep)) {
    next = { ...next, fieldRepNames: new Set([...next.fieldRepNames, newRep]) };
  }
  if (
    filters.statuses
    && updated.status
    && before.status !== updated.status
    && !filters.statuses.has(updated.status)
  ) {
    next = { ...next, statuses: new Set([...next.statuses, updated.status]) };
  }
  return next;
}

function isTicketLike(marker) {
  if (!marker) return false;
  if (marker._layerType === 'fieldRep' || marker._live) return false;
  if (marker._isStack && marker._stackMembers?.[0]?._layerType === 'fieldRep') return false;
  return true;
}

function ticketsFromMarker(marker) {
  if (!isTicketLike(marker)) return [];
  if (marker._isStack && marker._stackMembers) return marker._stackMembers.filter(m => m.recordId);
  return marker.recordId ? [marker] : [];
}

function ticketsFromSelection(marker) {
  return ticketsFromMarker(marker);
}

function selectionFromTickets(tickets) {
  if (!tickets || tickets.length === 0) return null;
  if (tickets.length === 1) return tickets[0];
  const first = tickets[0];
  return {
    id: 'multi-select',
    _isStack: true,
    _isMultiSelect: true,
    _stackMembers: tickets,
    _stackCount: tickets.length,
    siteLatitude: first.siteLatitude,
    siteLongitude: first.siteLongitude,
    latitude: first.siteLatitude,
    longitude: first.siteLongitude,
  };
}

/** Count non-default ticket/rep filters for the mobile FAB badge. */
function countActiveFilters(filters) {
  let n = 0;
  if ((filters.search || '').trim()) n += 1;
  if (filters.projectNames) n += 1;
  if (filters.serviceTypes) n += 1;
  if (filters.states) n += 1;
  if (filters.fscValues) n += 1;
  if (filters.fieldRepNames) n += 1;
  if (filters.statuses) n += 1;
  if (filters.preset) n += 1;
  if (filters.dueDateFrom || filters.dueDateTo) n += 1;
  if (filters.startDateFrom || filters.startDateTo) n += 1;
  if (filters.scheduleDateFrom || filters.scheduleDateTo) n += 1;
  if (filters.repPaymentMethod) n += 1;
  if (filters.repCategory) n += 1;
  if (filters.classification) n += 1;
  if ((filters.repMinJobs12mo || 0) > 0) n += 1;
  if (filters.repHistoryRepIds) n += 1;
  const tiers = filters.repRatingTiers;
  if (tiers && tiers.size < 6) n += 1;
  return n;
}

export default function CoordinatorMap() {
  const [tickets, setTickets] = useState([]);
  const [fieldReps, setFieldReps] = useState([]);
  const [iconConfig, setIconConfig] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [repLastSynced, setRepLastSynced] = useState(null);
  const [refreshingReps, setRefreshingReps] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [missingCoords, setMissingCoords] = useState(0);
  const [clusterMaxZoom, setClusterMaxZoom] = useState(14);
  const [labelBackground, setLabelBackground] = useState(true);
  const [ticketsLastFetched, setTicketsLastFetched] = useState(null);
  const [messagesLastFetched, setMessagesLastFetched] = useState(null);
  const [refreshingTickets, setRefreshingTickets] = useState(false);
  const [refreshingMessages, setRefreshingMessages] = useState(false);
  const [openMessageCounts, setOpenMessageCounts] = useState({});
  const [chatTickets, setChatTickets] = useState(null);
  const [chatAnchor, setChatAnchor] = useState(null);
  const [chatPoint, setChatPoint] = useState(null);

  // User label prefs (localStorage). Seeded from admin ticketLabelField on first visit.
  const storedLabelPrefsRef = useRef(loadLabelPrefs());
  const [labelPrefs, setLabelPrefs] = useState(
    () => storedLabelPrefsRef.current || DEFAULT_LABEL_PREFS
  );

  const updateLabelPrefs = useCallback((patch) => {
    setLabelPrefs(prev => {
      const next = { ...prev, ...patch };
      localStorage.setItem(LABEL_PREFS_KEY, JSON.stringify(next));
      storedLabelPrefsRef.current = next;
      return next;
    });
  }, []);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('coordSidebarWidth');
    return saved ? parseInt(saved, 10) : 320;
  });
  const isDragging = useRef(false);
  const [hiddenTypeSlugs, setHiddenTypeSlugs] = useState(loadHiddenTypeSlugs);
  const [markerMenu, setMarkerMenu] = useState(null);
  const [checkInTicket, setCheckInTicket] = useState(null);

  const [layerVisibility, setLayerVisibility] = useState(() => loadStoredLayers() || {
    unassigned: true,
    assigned: false,
    fieldReps: false,
    liveReps: true,   // default on when the feature is enabled
  });

  // Live rep location layer
  const [liveReps, setLiveReps] = useState([]);
  const [liveLocationEnabled, setLiveLocationEnabled] = useState(false);
  const [liveWindowHours, setLiveWindowHours] = useState(8); // recency window (0–24h)

  const [filters, setFiltersState] = useState(() => loadStoredFilters() || DEFAULT_FILTERS);
  const ticketsRef = useRef([]);
  ticketsRef.current = tickets;

  // Persist filters / layers for the browser tab so saves, polls, or brief
  // remounts never wipe the coordinator's active filter selection.
  const setFilters = useCallback((updater) => {
    setFiltersState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, serializeFilters(next));
      } catch { /* ignore quota / private mode */ }
      return next;
    });
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(LAYERS_STORAGE_KEY, JSON.stringify(layerVisibility));
    } catch { /* ignore */ }
  }, [layerVisibility]);

  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  // Leaving mobile layout should not leave drawer state stuck
  useEffect(() => {
    if (!isMobile) setFiltersOpen(false);
  }, [isMobile]);

  // Load all data on mount
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const [ticketResult, repResult, iconResult, adminResult, mapConfig, messageResult] = await Promise.all([
          fetchCoordinatorTickets(),
          fetchCoordinatorFieldReps(),
          fetchCoordinatorIcons(),
          checkCoordinatorAdmin().catch(() => ({ isAdmin: false, canEdit: false })),
          fetchMapConfig().catch(() => ({ clusterMaxZoom: 14 })),
          fetchOpenMessageCounts().catch(err => {
            console.warn('Open message counts failed:', err.message);
            return { counts: {}, lastFetched: null };
          }),
        ]);

        setTickets(ticketResult.tickets || []);
        setMissingCoords(ticketResult.missingCoords || 0);
        setTicketsLastFetched(ticketResult.lastFetched || new Date().toISOString());
        setOpenMessageCounts(messageResult.counts || {});
        setMessagesLastFetched(messageResult.lastFetched || null);
        setFieldReps(repResult.reps || []);
        setRepLastSynced(repResult.lastSynced);
        setIconConfig(iconResult);
        setIsAdmin(adminResult.isAdmin);
        setCanEdit(!!adminResult.canEdit);
        setClusterMaxZoom(mapConfig.clusterMaxZoom ?? 14);
        setLabelBackground(mapConfig.labelBackground ?? true);

        // First visit: seed ticket label field from admin default and persist
        if (!storedLabelPrefsRef.current) {
          const seeded = {
            ...DEFAULT_LABEL_PREFS,
            ticketLabelField: mapConfig.ticketLabelField ?? 'siteName',
          };
          setLabelPrefs(seeded);
          localStorage.setItem(LABEL_PREFS_KEY, JSON.stringify(seeded));
          storedLabelPrefsRef.current = seeded;
        }

        const liveEnabled = !!mapConfig.liveLocationEnabled;
        setLiveLocationEnabled(liveEnabled);
        if (liveEnabled) {
          fetchCoordinatorLiveReps()
            .then(r => setLiveReps(r.liveReps || []))
            .catch(() => {});
        }
      } catch (err) {
        console.error('Failed to load coordinator map data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Track user activity — only poll/trigger background refresh when active
  const lastActivityRef = useRef(Date.now());
  const ACTIVITY_TIMEOUT = 2 * 60 * 1000; // 2 minutes

  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('mousemove', markActive);
    window.addEventListener('click', markActive);
    window.addEventListener('keydown', markActive);
    window.addEventListener('scroll', markActive, true);
    return () => {
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('scroll', markActive, true);
    };
  }, []);

  // Poll for cache updates every 60 seconds (only when tab visible + user active)
  useEffect(() => {
    if (loading) return;
    const interval = setInterval(async () => {
      // Skip if tab is hidden
      if (document.hidden) return;
      // Skip if user has been idle for too long
      if (Date.now() - lastActivityRef.current > ACTIVITY_TIMEOUT) return;

      try {
        const isActive = Date.now() - lastActivityRef.current < ACTIVITY_TIMEOUT;
        const status = await fetchCacheStatus(isActive);
        // If tickets were refreshed by another user/instance, auto-update
        if (status.ticketsLastFetched && status.ticketsLastFetched !== ticketsLastFetched) {
          console.log('Ticket cache updated externally, refreshing...');
          const result = await fetchCoordinatorTickets();
          setTickets(result.tickets || []);
          setMissingCoords(result.missingCoords || 0);
          setTicketsLastFetched(result.lastFetched || status.ticketsLastFetched);
        }
        // Same for field reps
        if (status.repsLastSynced && status.repsLastSynced !== repLastSynced) {
          console.log('Field rep cache updated externally, refreshing...');
          const result = await fetchCoordinatorFieldReps();
          setFieldReps(result.reps || []);
          setRepLastSynced(result.lastSynced || status.repsLastSynced);
        }
        if (status.messagesLastFetched && status.messagesLastFetched !== messagesLastFetched) {
          const result = await fetchOpenMessageCounts();
          setOpenMessageCounts(result.counts || {});
          setMessagesLastFetched(result.lastFetched || status.messagesLastFetched);
        }
        // Pick up admin config changes (not user ticketLabelField — that lives in localStorage)
        if (status.clusterMaxZoom != null && status.clusterMaxZoom !== clusterMaxZoom) {
          setClusterMaxZoom(status.clusterMaxZoom);
        }
        if (typeof status.labelBackground === 'boolean' && status.labelBackground !== labelBackground) {
          setLabelBackground(status.labelBackground);
        }
        // Refresh live rep positions (last-known, ages between polls)
        if (liveLocationEnabled) {
          const live = await fetchCoordinatorLiveReps().catch(() => null);
          if (live) setLiveReps(live.liveReps || []);
        }
      } catch (err) {
        // Silent fail on poll — don't disrupt the user
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [loading, ticketsLastFetched, repLastSynced, messagesLastFetched, clusterMaxZoom, liveLocationEnabled, labelBackground]);

  // Refresh tickets (preserves filters)
  const handleRefreshTickets = useCallback(async () => {
    setRefreshingTickets(true);
    try {
      const result = await fetchCoordinatorTickets(true);
      setTickets(result.tickets || []);
      setMissingCoords(result.missingCoords || 0);
      setTicketsLastFetched(result.lastFetched || new Date().toISOString());
    } catch (err) {
      console.error('Failed to refresh tickets:', err);
    } finally {
      setRefreshingTickets(false);
    }
  }, []);

  // After a successful save, patch the ticket in local state without resetting
  // filters. Also keep the marker visible when assignment/status side-effects
  // would otherwise drop it out of the current layer / filter sets.
  const handleTicketUpdated = useCallback((updatedTicket) => {
    if (!updatedTicket) return;
    const before = ticketsRef.current.find(t => t.recordId === updatedTicket.recordId);

    setTickets(prev => prev.map(t => t.recordId === updatedTicket.recordId ? { ...t, ...updatedTicket } : t));
    // If the updated ticket is selected (alone or inside a stack), refresh that too
    setSelectedMarker(prev => {
      if (!prev) return prev;
      if (prev._isStack && prev._stackMembers) {
        let changed = false;
        const members = prev._stackMembers.map(m => {
          if (m.recordId !== updatedTicket.recordId) return m;
          changed = true;
          return { ...m, ...updatedTicket };
        });
        return changed ? { ...prev, _stackMembers: members } : prev;
      }
      return prev.recordId === updatedTicket.recordId ? { ...prev, ...updatedTicket } : prev;
    });

    if (before) {
      if (!before.fieldRepName && updatedTicket.fieldRepName) {
        setLayerVisibility(lv => (lv.assigned ? lv : { ...lv, assigned: true }));
      }
      setFilters(f => adjustFiltersAfterTicketUpdate(f, before, updatedTicket));
    }
  }, [setFilters]);

  const handleRefreshMessages = useCallback(async () => {
    setRefreshingMessages(true);
    try {
      const result = await fetchOpenMessageCounts(true);
      setOpenMessageCounts(result.counts || {});
      setMessagesLastFetched(result.lastFetched || new Date().toISOString());
    } catch (err) {
      console.error('Failed to refresh messages:', err);
    } finally {
      setRefreshingMessages(false);
    }
  }, []);

  // Refresh field reps
  const handleRefreshReps = useCallback(async () => {
    setRefreshingReps(true);
    try {
      const result = await refreshCoordinatorFieldReps();
      setFieldReps(result.reps || []);
      setRepLastSynced(result.lastSynced);
    } catch (err) {
      console.error('Failed to refresh field reps:', err);
    } finally {
      setRefreshingReps(false);
    }
  }, []);

  // Apply filters and layer visibility to compute visible markers
  const { visibleTickets, visibleReps } = useMemo(() => {
    // Filter tickets
    let filteredTickets = tickets;

    // Search bar (case-insensitive substring match across record ID,
    // site name, description, city, FN WO#, assigned field rep name,
    // and checked-in tech). Tokenized on whitespace — every token must
    // match somewhere in the indexed fields.
    const search = (filters.search || '').trim().toLowerCase();
    const searchTokens = search ? search.split(/\s+/) : null;
    if (searchTokens) {
      filteredTickets = filteredTickets.filter(t => {
        const haystack = [
          String(t.recordId ?? ''),
          t.siteName,
          t.description,
          t.city,
          t.fnWoId,
          t.fieldRepName,
          t.checkIn?.repName,
        ].filter(Boolean).join(' ').toLowerCase();
        return searchTokens.every(tok => haystack.includes(tok));
      });
    }

    if (filters.projectNames) {
      filteredTickets = filteredTickets.filter(t => filters.projectNames.has(t.projectName));
    }
    if (filters.serviceTypes) {
      filteredTickets = filteredTickets.filter(t => filters.serviceTypes.has(t.serviceType));
    }
    if (filters.states) {
      filteredTickets = filteredTickets.filter(t => filters.states.has(t.state));
    }
    if (filters.fscValues) {
      filteredTickets = filteredTickets.filter(t => filters.fscValues.has(t.fsc || ''));
    }
    if (filters.fieldRepNames) {
      filteredTickets = filteredTickets.filter(t => filters.fieldRepNames.has(t.fieldRepName || ''));
    }
    if (filters.statuses) {
      filteredTickets = filteredTickets.filter(t => filters.statuses.has(t.status));
    }
    if (filters.dueDateFrom) {
      filteredTickets = filteredTickets.filter(t => t.dueDate && t.dueDate >= filters.dueDateFrom);
    }
    if (filters.dueDateTo) {
      filteredTickets = filteredTickets.filter(t => t.dueDate && t.dueDate <= filters.dueDateTo);
    }
    if (filters.startDateFrom) {
      filteredTickets = filteredTickets.filter(t => t.startDate && t.startDate >= filters.startDateFrom);
    }
    if (filters.startDateTo) {
      filteredTickets = filteredTickets.filter(t => t.startDate && t.startDate <= filters.startDateTo);
    }
    if (filters.scheduleDateFrom) {
      filteredTickets = filteredTickets.filter(t => {
        const d = dateKey(t.scheduleDate);
        return d && d >= filters.scheduleDateFrom;
      });
    }
    if (filters.scheduleDateTo) {
      filteredTickets = filteredTickets.filter(t => {
        const d = dateKey(t.scheduleDate);
        return d && d <= filters.scheduleDateTo;
      });
    }
    if (filters.preset === 'scheduledToday') {
      const today = pacificToday();
      filteredTickets = filteredTickets.filter(t => dateKey(t.scheduleDate) === today);
    } else if (filters.preset === 'pastDue') {
      const today = pacificToday();
      filteredTickets = filteredTickets.filter(t => {
        const due = dateKey(t.dueDate);
        return due && due < today;
      });
    } else if (filters.preset === 'checkedIn') {
      filteredTickets = filteredTickets.filter(t => t.checkIn);
    }

    // Resolve icons and tag layer type
    // Add cache-bust param to icon URLs to avoid stale CORS responses
    filteredTickets = filteredTickets.map(t => {
      const { iconUrl, visible } = resolveTicketIcon(t, iconConfig);
      return {
        ...t,
        _layerType: t.fieldRepName ? 'assigned' : 'unassigned',
        _iconUrl: iconUrl,
        _iconVisible: visible,
        _openMessages: openMessageCounts[t.recordId] || 0,
      };
    });

    // Filter field reps
    let filteredReps = fieldReps;
    if (filters.repPaymentMethod) {
      filteredReps = filteredReps.filter(r => filters.repPaymentMethod.has(r.paymentMethod));
    }
    if (filters.repCategory) {
      filteredReps = filteredReps.filter(r => splitMulti(r.repCategory).some(v => filters.repCategory.has(v)));
    }
    if (filters.classification) {
      filteredReps = filteredReps.filter(r => {
        const vals = splitMulti(r.classification);
        if (vals.length === 0) return filters.classification.has(NO_VALUE);
        return vals.some(v => filters.classification.has(v));
      });
    }
    if (filters.repMinJobs12mo > 0) {
      filteredReps = filteredReps.filter(r => (parseInt(r.last12Months, 10) || 0) >= filters.repMinJobs12mo);
    }
    if (filters.repHistoryRepIds) {
      filteredReps = filteredReps.filter(r => filters.repHistoryRepIds.has(Number(r.repId)));
    }
    // Rating tier filter
    filteredReps = filteredReps.filter(r => {
      const rating = parseFloat(r.rating) || 0;
      let tier;
      if (rating >= 4.5) tier = 5;
      else if (rating >= 3.5) tier = 4;
      else if (rating >= 2.5) tier = 3;
      else if (rating >= 1.5) tier = 2;
      else if (rating >= 0.5) tier = 1;
      else tier = 0;
      return filters.repRatingTiers.has(tier);
    });

    // When search is active, narrow reps to matches on name / repId.
    if (searchTokens) {
      filteredReps = filteredReps.filter(r => {
        const haystack = [String(r.repId ?? ''), r.name].filter(Boolean).join(' ').toLowerCase();
        return searchTokens.every(tok => haystack.includes(tok));
      });
    }

    // Completed-project history filter: isolate the map to matching reps only
    // (hide tickets so the viewport is just the filtered result).
    const historyFilterActive = Boolean(filters.repHistoryRepIds);
    const visibleTickets = historyFilterActive
      ? []
      : filteredTickets.filter(t => {
          if (t._iconVisible === false) return false;
          if (hiddenTypeSlugs.has(slugifyTicketType(t.projectName, t.serviceType, t.itemName))) return false;
          if (t._layerType === 'unassigned' && !layerVisibility.unassigned) return false;
          if (t._layerType === 'assigned' && !layerVisibility.assigned) return false;
          return true;
        });

    const visibleReps = layerVisibility.fieldReps
      ? filteredReps.map(r => ({ ...r, _layerType: 'fieldRep' }))
      : [];

    return { visibleTickets, visibleReps };
  }, [tickets, fieldReps, filters, layerVisibility, iconConfig, openMessageCounts, hiddenTypeSlugs]);

  // Live rep positions: apply the recency window + layer visibility, and join
  // to the full rep record so a click opens the normal Field Rep detail panel.
  // Hidden while a completed-project history filter is isolating the map.
  const visibleLiveReps = useMemo(() => {
    if (filters.repHistoryRepIds) return [];
    if (!liveLocationEnabled || !layerVisibility.liveReps) return [];
    const repById = new Map(fieldReps.map(r => [r.repId, r]));
    const maxAge = liveWindowHours * 60;
    return liveReps
      .filter(lr => (lr.ageMinutes ?? Infinity) <= maxAge)
      .map(lr => {
        const full = repById.get(lr.repId);
        return {
          ...(full || {}),
          ...lr,                    // live coords / age / id win over the home record
          _layerType: 'fieldRep',   // reuse the rep detail panel on click
          _live: true,
        };
      });
  }, [liveReps, liveLocationEnabled, layerVisibility.liveReps, liveWindowHours, fieldReps, filters.repHistoryRepIds]);

  // Per-layer search hit detection (computed off base data, regardless of
  // current visibility) — used to auto-on layers that have hits while
  // search is active.
  const searchActive = (filters.search || '').trim() !== '';

  const labelVisibility = useMemo(() => ({
    tickets: labelPrefs.ticketLabels,
    fieldReps: labelPrefs.fieldRepLabels,
    liveReps: labelPrefs.liveRepLabels,
  }), [labelPrefs.ticketLabels, labelPrefs.fieldRepLabels, labelPrefs.liveRepLabels]);

  const searchHits = useMemo(() => {
    if (!searchActive) return null;
    const tokens = filters.search.trim().toLowerCase().split(/\s+/);
    let unassigned = false, assigned = false, reps = false;
    for (const t of tickets) {
      if (assigned && unassigned) break;
      const haystack = [
        String(t.recordId ?? ''),
        t.siteName,
        t.description,
        t.city,
        t.fnWoId,
        t.fieldRepName,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!tokens.every(tok => haystack.includes(tok))) continue;
      if (t.fieldRepName) assigned = true;
      else unassigned = true;
    }
    for (const r of fieldReps) {
      if (reps) break;
      const haystack = [String(r.repId ?? ''), r.name].filter(Boolean).join(' ').toLowerCase();
      if (tokens.every(tok => haystack.includes(tok))) reps = true;
    }
    return { unassigned, assigned, reps };
  }, [searchActive, filters.search, tickets, fieldReps]);

  // Snapshot pre-search layer visibility so we can restore on clear.
  // While active, layers that have hits get forced on (additive — never
  // turns a layer off). Manual toggles between query changes stick, but
  // if the query changes again the OR with snapshot can re-enable a
  // layer the user just turned off; acceptable cost of always restoring
  // cleanly on clear.
  const prevLayerVisRef = useRef(null);
  useEffect(() => {
    if (searchActive) {
      if (prevLayerVisRef.current === null) {
        prevLayerVisRef.current = layerVisibility;
      }
      if (searchHits) {
        const prev = prevLayerVisRef.current;
        const next = {
          ...prev, // preserve other layer keys (e.g. liveReps)
          unassigned: prev.unassigned || searchHits.unassigned,
          assigned: prev.assigned || searchHits.assigned,
          fieldReps: prev.fieldReps || searchHits.reps,
        };
        if (
          next.unassigned !== layerVisibility.unassigned ||
          next.assigned !== layerVisibility.assigned ||
          next.fieldReps !== layerVisibility.fieldReps
        ) {
          setLayerVisibility(next);
        }
      }
    } else if (prevLayerVisRef.current !== null) {
      setLayerVisibility(prevLayerVisRef.current);
      prevLayerVisRef.current = null;
    }
    // layerVisibility intentionally omitted — including it would re-fire
    // the effect on every snap and fight user toggles. searchHits is the
    // signal that should drive auto-on changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchActive, searchHits]);

  // Completed-project history filter: force Field Reps layer on so the
  // isolated result is visible, then fit the map to those reps.
  const historyFilterActive = Boolean(filters.repHistoryRepIds);
  const historyFitKey = historyFilterActive
    ? [
        filters.repHistoryProjectId,
        filters.repHistoryDateFrom || '',
        filters.repHistoryDateTo || '',
        filters.repHistoryRepIds.size,
      ].join('|')
    : '';

  useEffect(() => {
    if (!historyFilterActive) return;
    setLayerVisibility(prev => (prev.fieldReps ? prev : { ...prev, fieldReps: true }));
  }, [historyFilterActive]);

  const handleMessageClick = useCallback((marker) => {
    const members = marker._isStack && marker._stackMembers ? marker._stackMembers : [marker];
    const withMessages = members.filter(m => (m._openMessages || 0) > 0 && m.recordId);
    const list = (withMessages.length ? withMessages : members.filter(m => m.recordId));
    if (!list.length) return;
    const lat = marker.siteLatitude ?? marker.latitude;
    const lng = marker.siteLongitude ?? marker.longitude;
    setChatAnchor(Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null);
    setChatTickets(list.map(m => ({
      recordId: m.recordId,
      siteName: m.siteName || '',
      itemName: m.itemName || '',
      openCount: m._openMessages || 0,
      relatedFieldRepId: m.relatedFieldRepId || null,
      fieldRepName: m.fieldRepName || '',
    })));
  }, []);

  const handleChatPoint = useCallback((point) => {
    setChatPoint(prev => {
      if (!point) return prev ? null : prev;
      if (
        prev
        && Math.abs(prev.x - point.x) < 0.5
        && Math.abs(prev.y - point.y) < 0.5
        && prev.width === point.width
        && prev.height === point.height
      ) return prev;
      return point;
    });
  }, []);

  const closeChat = useCallback(() => {
    setChatTickets(null);
    setChatAnchor(null);
    setChatPoint(null);
  }, []);

  const handleThreadCreated = useCallback((recordId) => {
    setOpenMessageCounts(prev => {
      const next = { ...prev };
      const key = String(recordId);
      next[key] = Number(next[key] || 0) + 1;
      return next;
    });
    setChatTickets(prev => {
      if (!prev) return prev;
      return prev.map(t => (
        t.recordId === recordId ? { ...t, openCount: (t.openCount || 0) + 1 } : t
      ));
    });
  }, []);

  const handleThreadClosed = useCallback((recordId) => {
    setOpenMessageCounts(prev => {
      const next = { ...prev };
      const key = String(recordId);
      const n = Math.max(0, Number(next[key] || 0) - 1);
      if (n === 0) delete next[key];
      else next[key] = n;
      return next;
    });
    setChatTickets(prev => {
      if (!prev) return prev;
      return prev.map(t => (
        t.recordId === recordId ? { ...t, openCount: Math.max(0, (t.openCount || 1) - 1) } : t
      ));
    });
  }, []);

  const handleMarkerClick = useCallback((marker, { shiftKey, point, rightClick } = {}) => {
    if (rightClick) {
      if (point) setMarkerMenu({ marker, point });
      return;
    }
    // Exclusive on mobile: selecting a marker closes the filter drawer
    setFiltersOpen(false);
    setMarkerMenu(null);

    const incomingTickets = ticketsFromMarker(marker);
    setSelectedMarker(prev => {
      const current = ticketsFromSelection(prev);
      // Shift-click / long-press always toggles. Once 2+ tickets are selected,
      // a regular ticket tap also toggles so mobile can add/remove without Shift.
      const additive = incomingTickets.length > 0 && (shiftKey || current.length > 1);
      if (additive) {
        const incomingIds = new Set(incomingTickets.map(t => t.recordId));
        const allIncomingSelected = incomingTickets.every(t =>
          current.some(c => c.recordId === t.recordId)
        );
        let next;
        if (allIncomingSelected) {
          next = current.filter(c => !incomingIds.has(c.recordId));
        } else {
          const seen = new Set(current.map(t => t.recordId));
          next = [...current, ...incomingTickets.filter(t => !seen.has(t.recordId))];
        }
        return selectionFromTickets(next);
      }
      if (marker._isStack) {
        return {
          ...marker,
          _isStack: true,
          _stackMembers: marker._stackMembers,
        };
      }
      return marker;
    });
  }, []);

  const handleLassoSelect = useCallback((lassoTickets) => {
    setMarkerMenu(null);
    if (!lassoTickets || lassoTickets.length === 0) return;
    setFiltersOpen(false);
    setSelectedMarker(prev => {
      const current = ticketsFromSelection(prev);
      const seen = new Set(current.map(t => t.recordId));
      const next = [...current];
      for (const t of lassoTickets) {
        if (t.recordId && !seen.has(t.recordId)) {
          seen.add(t.recordId);
          next.push(t);
        }
      }
      return selectionFromTickets(next);
    });
  }, []);

  const handleSelectionChange = useCallback((tickets) => {
    setSelectedMarker(selectionFromTickets(tickets));
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedMarker(null);
    setMarkerMenu(null);
    closeChat();
  }, [closeChat]);

  const hideTicketTypes = useCallback((slugs) => {
    setHiddenTypeSlugs(prev => {
      const next = new Set(prev);
      for (const slug of slugs) next.add(slug);
      persistHiddenTypes(next);
      return next;
    });
    setSelectedMarker(null);
    closeChat();
  }, [closeChat]);

  const showOnlyTicketTypes = useCallback((slugs) => {
    const keep = new Set(slugs);
    setHiddenTypeSlugs(() => {
      const next = new Set();
      for (const ticket of tickets) {
        const slug = slugifyTicketType(ticket.projectName, ticket.serviceType, ticket.itemName);
        if (slug && !keep.has(slug)) next.add(slug);
      }
      persistHiddenTypes(next);
      return next;
    });
    setSelectedMarker(null);
    closeChat();
  }, [tickets, closeChat]);

  const restoreHiddenTypes = useCallback(() => {
    setHiddenTypeSlugs(new Set());
    try { sessionStorage.removeItem(HIDDEN_TYPES_KEY); } catch { /* ignore */ }
  }, []);

  const applyPreset = useCallback((id) => {
    if (id === 'suggest') {
      window.dispatchEvent(new CustomEvent('gems:open-feedback', {
        detail: { category: 'suggestion', message: 'Filter preset idea: ' },
      }));
      return;
    }
    if (id === 'scheduledToday' || id === 'pastDue' || id === 'checkedIn') {
      setLayerVisibility(prev => ({ ...prev, unassigned: true, assigned: true, fieldReps: false }));
      setFilters(prev => ({ ...prev, preset: id }));
      return;
    }
    setFilters(prev => (prev.preset ? { ...prev, preset: null } : prev));
    if (id === 'all') {
      setLayerVisibility(prev => ({ ...prev, unassigned: true, assigned: true, fieldReps: false }));
    } else if (id === 'unassigned') {
      setLayerVisibility(prev => ({ ...prev, unassigned: true, assigned: false, fieldReps: false }));
    } else if (id === 'fieldReps') {
      setLayerVisibility(prev => ({ ...prev, unassigned: false, assigned: false, fieldReps: true }));
    }
  }, [setFilters]);

  const activePreset = filters.preset
    || (layerVisibility.unassigned && layerVisibility.assigned && !layerVisibility.fieldReps ? 'all' : null)
    || (layerVisibility.unassigned && !layerVisibility.assigned && !layerVisibility.fieldReps ? 'unassigned' : null)
    || (!layerVisibility.unassigned && !layerVisibility.assigned && layerVisibility.fieldReps ? 'fieldReps' : null);

  const selectedIds = useMemo(() => {
    if (!selectedMarker) return new Set();
    const members = ticketsFromSelection(selectedMarker);
    if (members.length > 0) {
      return new Set(members.map(m => m.id).filter(Boolean));
    }
    return selectedMarker.id ? new Set([selectedMarker.id]) : new Set();
  }, [selectedMarker]);

  const multiTicketSelected = ticketsFromSelection(selectedMarker).length > 1;

  const openFilters = useCallback(() => {
    // Exclusive: opening filters closes detail
    setSelectedMarker(null);
    setFiltersOpen(true);
  }, []);

  const closeFilters = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  // Sidebar resize handlers — desktop only (handle hidden on mobile)
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const newWidth = Math.max(240, Math.min(600, e.clientX));
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('coordSidebarWidth', String(sidebarWidth));
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  if (loading) {
    return (
      <div className="coordinator-map-page">
        <div className="coord-loading">
          <div className="coord-loading-spinner" />
          <p>Loading Coordinator Map...</p>
          <p className="coord-loading-sub">Fetching tickets and field rep data from QuickBase</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="coordinator-map-page">
        <div className="coord-error">
          <h3>Failed to load map data</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        'coordinator-map-page',
        isMobile && filtersOpen ? 'mobile-filters-open' : '',
        isMobile && selectedMarker ? 'mobile-detail-open' : '',
      ].filter(Boolean).join(' ')}
      style={isMobile ? undefined : { '--coord-sidebar-width': `${sidebarWidth}px` }}
    >
      {/* Sidebar / mobile filter drawer */}
      <div className="coord-sidebar" style={isMobile ? undefined : { width: sidebarWidth }}>
        <div className="coord-sidebar-header">
          <div>
            <h2>Coordinator Map</h2>
            <div className="coord-stats">
              {visibleTickets.length} tickets &middot; {visibleReps.length} reps
              {missingCoords > 0 && (
                <span className="missing-coords" title={`${missingCoords} tickets have no coordinates`}>
                  ({missingCoords} no coords)
                </span>
              )}
            </div>
            {isAdmin && (
              <Link to="/coordinator-map/settings" className="coord-settings-link" title="Map Settings">
                Settings
              </Link>
            )}
          </div>
          {isMobile && (
            <button
              type="button"
              className="coord-mobile-drawer-close"
              onClick={closeFilters}
              aria-label="Close filters"
            >
              ×
            </button>
          )}
        </div>

        <CoordinatorFilterPanel
          tickets={tickets}
          fieldReps={fieldReps}
          layerVisibility={layerVisibility}
          onLayerVisibilityChange={setLayerVisibility}
          liveLocationEnabled={liveLocationEnabled}
          liveRepCount={visibleLiveReps.length}
          liveWindowHours={liveWindowHours}
          onLiveWindowChange={setLiveWindowHours}
          labelPrefs={labelPrefs}
          onLabelPrefsChange={updateLabelPrefs}
          filters={filters}
          onFiltersChange={setFilters}
          ticketsLastFetched={ticketsLastFetched}
          onRefreshTickets={handleRefreshTickets}
          refreshingTickets={refreshingTickets}
          repLastSynced={repLastSynced}
          onRefreshReps={handleRefreshReps}
          refreshingReps={refreshingReps}
          messagesLastFetched={messagesLastFetched}
          onRefreshMessages={handleRefreshMessages}
          refreshingMessages={refreshingMessages}
          activePreset={activePreset}
          onApplyPreset={applyPreset}
          hiddenCount={hiddenTypeSlugs.size}
          onRestoreHidden={restoreHiddenTypes}
        />
      </div>

      {/* Resize handle — desktop only */}
      {!isMobile && (
        <div className="coord-resize-handle" onMouseDown={handleResizeStart} />
      )}

      {/* Marker detail — column on desktop, bottom sheet on mobile */}
      <MarkerDetailColumn
        selectedMarker={selectedMarker}
        fieldReps={fieldReps}
        canEdit={canEdit}
        onClearSelection={handleClearSelection}
        onTicketUpdated={handleTicketUpdated}
        onSelectionChange={handleSelectionChange}
        onOpenFilters={isMobile ? openFilters : undefined}
        openMessageCounts={openMessageCounts}
        onThreadClosed={handleThreadClosed}
        onThreadCreated={handleThreadCreated}
      />

      {/* Map */}
      <div className="coord-map-area">
        {isMobile && (
          <div className="coord-mobile-stats">
            <span className="title">Coordinator Map</span>
            <span className="meta">
              {visibleTickets.length} tickets · {visibleReps.length} reps
            </span>
          </div>
        )}

        <CoordinatorDeckGLMap
          tickets={visibleTickets}
          fieldReps={visibleReps}
          liveReps={visibleLiveReps}
          selectedIds={selectedIds}
          selectedMarker={selectedMarker}
          onMarkerClick={handleMarkerClick}
          onMessageClick={handleMessageClick}
          chatAnchor={isMobile ? null : chatAnchor}
          onChatPoint={isMobile ? null : handleChatPoint}
          onLassoSelect={handleLassoSelect}
          onDeselect={handleClearSelection}
          lockClickDeselect={multiTicketSelected}
          iconConfig={iconConfig}
          clusterMaxZoom={clusterMaxZoom}
          ticketLabelField={labelPrefs.ticketLabelField}
          labelBackground={labelBackground}
          labelVisibility={labelVisibility}
          searchActive={searchActive || historyFilterActive}
          searchQuery={historyFilterActive ? historyFitKey : (filters.search || '').trim()}
          mobileDetailOpen={isMobile && !!selectedMarker}
        />

        {chatTickets && (
          <TicketChatPanel
            tickets={chatTickets}
            canEdit={canEdit}
            onClose={closeChat}
            onThreadClosed={handleThreadClosed}
            onThreadCreated={handleThreadCreated}
            fieldReps={fieldReps}
            screenPoint={isMobile ? null : chatPoint}
          />
        )}

        {isMobile && (
          <button
            type="button"
            className="coord-filters-fab"
            onClick={openFilters}
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="badge" data-count={activeFilterCount}>{activeFilterCount}</span>
            )}
          </button>
        )}
      </div>

      {/* Mobile filter drawer backdrop */}
      {isMobile && (
        <button
          type="button"
          className="coord-mobile-backdrop"
          aria-label="Close filters"
          onClick={closeFilters}
        />
      )}

      {markerMenu && (
        <MarkerActionMenu
          marker={markerMenu.marker}
          point={markerMenu.point}
          canEdit={canEdit}
          onClose={() => setMarkerMenu(null)}
          onSendMessage={handleMessageClick}
          onRequestCheckIn={setCheckInTicket}
          onHideTypes={hideTicketTypes}
          onShowOnlyTypes={showOnlyTicketTypes}
          onHideReps={(marker) => {
            setLayerVisibility(prev => (
              marker?._live
                ? { ...prev, liveReps: false }
                : { ...prev, fieldReps: false }
            ));
            setSelectedMarker(null);
          }}
        />
      )}

      {checkInTicket && (
        <CheckInRequestDialog
          ticket={checkInTicket}
          onClose={() => setCheckInTicket(null)}
          onSaved={(ticket) => {
            handleTicketUpdated(ticket);
            setCheckInTicket(null);
          }}
        />
      )}
    </div>
  );
}
